'use client';
import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import type { ChatMentionsMenuItemType } from './types';
import type { MentionEntity } from '@terra/shared-dto-chat-types';
import type { MentionEntityWithOffset } from '../editor/lexical';

type MentionClickHandler = (mention: MentionEntity) => void;
type InsertMentionInEditorHandler = (entity: MentionEntity) => boolean;
type GetMentionsFromEditorHandler = () => MentionEntityWithOffset[];

type ChatMentionsContextType = {
  selectedMentions: ChatMentionsMenuItemType[];
  addMention: (mention: ChatMentionsMenuItemType) => void;
  removeMention: (mentionId: string) => void;
  removeMentionByEntityId: (entityId: string) => void;
  clearMentions: () => void;
  isMentionSelected: (mentionId: string) => boolean;
  setSelectedMentions: (mentions: ChatMentionsMenuItemType[]) => void;
  onMentionClick?: MentionClickHandler;
  setOnMentionClick: (handler: MentionClickHandler | undefined) => void;
  setInsertMentionInEditor: (
    handler: InsertMentionInEditorHandler | undefined,
  ) => void;
  insertMentionInEditor: (entity: MentionEntity) => void;
  getMentionsFromEditor: () => MentionEntityWithOffset[];
  setGetMentionsFromEditor: (
    handler: GetMentionsFromEditorHandler | undefined,
  ) => void;
  syncMentionsWithEditor: (editorEntityIds: Set<string>) => void;
  restoreSelectedMentionsForHistory: (
    mentions: ChatMentionsMenuItemType[],
  ) => void;
};

const ChatMentionsContext = createContext<ChatMentionsContextType | undefined>(
  undefined,
);

function toEditorMentionEntity(
  mention: ChatMentionsMenuItemType,
): MentionEntity | undefined {
  if (!mention.mentionEntity) {
    return undefined;
  }
  return {
    ...mention.mentionEntity,
    extraData: {
      ...mention.mentionEntity.extraData,
      mentionMenuItemId: mention.id,
    },
  };
}

function pruneEditorInsertedMentionIds({
  editorInsertedMentionIdsRef,
  mentions,
}: {
  editorInsertedMentionIdsRef: { current: Set<string> };
  mentions: ChatMentionsMenuItemType[];
}) {
  const mentionIds = new Set(mentions.map((mention) => mention.id));
  for (const mentionId of editorInsertedMentionIdsRef.current) {
    if (!mentionIds.has(mentionId)) {
      editorInsertedMentionIdsRef.current.delete(mentionId);
    }
  }
}

function flushMentionsToEditor({
  mentions,
  insertHandler,
  editorInsertedMentionIdsRef,
  getEditorEntityIds,
}: {
  mentions: ChatMentionsMenuItemType[];
  insertHandler: InsertMentionInEditorHandler;
  editorInsertedMentionIdsRef: { current: Set<string> };
  getEditorEntityIds?: () => Set<string>;
}) {
  const editorEntityIds = getEditorEntityIds?.() ?? new Set<string>();

  for (const mention of mentions) {
    const entityId = mention.mentionEntity?.entityId;
    if (editorInsertedMentionIdsRef.current.has(mention.id)) {
      continue;
    }
    if (entityId && editorEntityIds.has(entityId)) {
      editorInsertedMentionIdsRef.current.add(mention.id);
      continue;
    }
    const entity = toEditorMentionEntity(mention);
    if (!entity) {
      continue;
    }
    if (insertHandler(entity)) {
      editorInsertedMentionIdsRef.current.add(mention.id);
    }
  }
}

type ChatMentionsProviderProps = {
  children: ReactNode;
  initialMentions?: ChatMentionsMenuItemType[];
  onMentionClick?: MentionClickHandler;
};

export function ChatMentionsProvider({
  children,
  initialMentions = [],
  onMentionClick: initialOnMentionClick,
}: ChatMentionsProviderProps) {
  const [selectedMentions, setSelectedMentionsState] =
    useState<ChatMentionsMenuItemType[]>(initialMentions);

  const selectedMentionIdsRef = useRef(
    new Set(initialMentions.map((m) => m.id)),
  );
  const editorInsertedMentionIdsRef = useRef(new Set<string>());

  const onMentionClickRef = useRef<MentionClickHandler | undefined>(
    initialOnMentionClick,
  );
  const insertMentionInEditorRef = useRef<
    InsertMentionInEditorHandler | undefined
  >(undefined);
  const getMentionsFromEditorRef = useRef<
    GetMentionsFromEditorHandler | undefined
  >(undefined);
  const selectedMentionsRef = useRef(selectedMentions);

  selectedMentionsRef.current = selectedMentions;

  const setSelectedMentions = useCallback(
    (mentions: ChatMentionsMenuItemType[]) => {
      selectedMentionIdsRef.current = new Set(mentions.map((m) => m.id));
      pruneEditorInsertedMentionIds({
        editorInsertedMentionIdsRef,
        mentions,
      });
      setSelectedMentionsState(mentions);
      const insertHandler = insertMentionInEditorRef.current;
      if (insertHandler) {
        flushMentionsToEditor({
          mentions,
          insertHandler,
          editorInsertedMentionIdsRef,
          getEditorEntityIds: () =>
            new Set(
              (getMentionsFromEditorRef.current?.() ?? []).map(
                (mention) => mention.entityId,
              ),
            ),
        });
      }
    },
    [],
  );

  const addMention = useCallback((mention: ChatMentionsMenuItemType) => {
    const alreadyInState = selectedMentionIdsRef.current.has(mention.id);

    if (!alreadyInState) {
      selectedMentionIdsRef.current.add(mention.id);
      setSelectedMentionsState((prev) => [...prev, mention]);
    }

    if (editorInsertedMentionIdsRef.current.has(mention.id)) {
      return;
    }

    const insertHandler = insertMentionInEditorRef.current;
    const entity = toEditorMentionEntity(mention);
    if (!insertHandler || !entity) {
      return;
    }

    const entityId = mention.mentionEntity?.entityId;
    const editorEntityIds = new Set(
      (getMentionsFromEditorRef.current?.() ?? []).map(
        (editorMention) => editorMention.entityId,
      ),
    );
    if (entityId && editorEntityIds.has(entityId)) {
      editorInsertedMentionIdsRef.current.add(mention.id);
      return;
    }

    if (insertHandler(entity)) {
      editorInsertedMentionIdsRef.current.add(mention.id);
    }
  }, []);

  const removeMention = useCallback((mentionId: string) => {
    selectedMentionIdsRef.current.delete(mentionId);
    editorInsertedMentionIdsRef.current.delete(mentionId);
    setSelectedMentionsState((prev) => prev.filter((m) => m.id !== mentionId));
  }, []);

  const removeMentionByEntityId = useCallback((entityId: string) => {
    setSelectedMentionsState((prev) => {
      const next = prev.filter((m) => m.mentionEntity?.entityId !== entityId);
      const removed = prev.filter(
        (m) => m.mentionEntity?.entityId === entityId,
      );
      selectedMentionIdsRef.current = new Set(next.map((m) => m.id));
      for (const m of removed) {
        editorInsertedMentionIdsRef.current.delete(m.id);
      }
      return next;
    });
  }, []);

  const clearMentions = useCallback(() => {
    selectedMentionIdsRef.current.clear();
    editorInsertedMentionIdsRef.current.clear();
    setSelectedMentionsState((previousMentions) =>
      previousMentions.length === 0 ? previousMentions : [],
    );
  }, []);

  const isMentionSelected = useCallback(
    (mentionId: string) => {
      return selectedMentions.some((m) => m.id === mentionId);
    },
    [selectedMentions],
  );

  const setOnMentionClick = useCallback(
    (handler: MentionClickHandler | undefined) => {
      onMentionClickRef.current = handler;
    },
    [],
  );

  const setInsertMentionInEditor = useCallback(
    (handler: InsertMentionInEditorHandler | undefined) => {
      insertMentionInEditorRef.current = handler;
      if (!handler) {
        return;
      }
      flushMentionsToEditor({
        mentions: selectedMentionsRef.current,
        insertHandler: handler,
        editorInsertedMentionIdsRef,
        getEditorEntityIds: () =>
          new Set(
            (getMentionsFromEditorRef.current?.() ?? []).map(
              (mention) => mention.entityId,
            ),
          ),
      });
    },
    [],
  );

  const insertMentionInEditor = useCallback((entity: MentionEntity) => {
    insertMentionInEditorRef.current?.(entity);
  }, []);

  const getMentionsFromEditor = useCallback(
    (): MentionEntityWithOffset[] => getMentionsFromEditorRef.current?.() ?? [],
    [],
  );

  const setGetMentionsFromEditor = useCallback(
    (handler: GetMentionsFromEditorHandler | undefined) => {
      getMentionsFromEditorRef.current = handler;
    },
    [],
  );

  const syncMentionsWithEditor = useCallback((editorEntityIds: Set<string>) => {
    setSelectedMentionsState((prev) => {
      const next: ChatMentionsMenuItemType[] = [];
      const removed: ChatMentionsMenuItemType[] = [];

      for (const mention of prev) {
        const entityId = mention.mentionEntity?.entityId;
        const keepInState =
          !entityId ||
          editorEntityIds.has(entityId) ||
          !editorInsertedMentionIdsRef.current.has(mention.id);

        if (keepInState) {
          next.push(mention);
        } else {
          removed.push(mention);
        }
      }

      if (removed.length === 0) {
        return prev;
      }

      selectedMentionIdsRef.current = new Set(
        next.map((mention) => mention.id),
      );
      for (const mention of removed) {
        editorInsertedMentionIdsRef.current.delete(mention.id);
      }
      return next;
    });
  }, []);

  const restoreSelectedMentionsForHistory = useCallback(
    (mentions: ChatMentionsMenuItemType[]) => {
      selectedMentionIdsRef.current = new Set(
        mentions.map((mention) => mention.id),
      );
      editorInsertedMentionIdsRef.current = new Set(
        mentions.map((mention) => mention.id),
      );
      setSelectedMentionsState(mentions);
    },
    [],
  );

  const handleMentionClick = useCallback((mention: MentionEntity) => {
    onMentionClickRef.current?.(mention);
  }, []);

  const contextValue = useMemo<ChatMentionsContextType>(
    () => ({
      selectedMentions,
      addMention,
      removeMention,
      removeMentionByEntityId,
      clearMentions,
      isMentionSelected,
      setSelectedMentions,
      onMentionClick: handleMentionClick,
      setOnMentionClick,
      setInsertMentionInEditor,
      insertMentionInEditor,
      getMentionsFromEditor,
      setGetMentionsFromEditor,
      syncMentionsWithEditor,
      restoreSelectedMentionsForHistory,
    }),
    [
      selectedMentions,
      addMention,
      removeMention,
      removeMentionByEntityId,
      clearMentions,
      isMentionSelected,
      setSelectedMentions,
      handleMentionClick,
      setOnMentionClick,
      setInsertMentionInEditor,
      insertMentionInEditor,
      getMentionsFromEditor,
      setGetMentionsFromEditor,
      syncMentionsWithEditor,
      restoreSelectedMentionsForHistory,
    ],
  );

  return (
    <ChatMentionsContext.Provider value={contextValue}>
      {children}
    </ChatMentionsContext.Provider>
  );
}

export function useMentions() {
  const context = useContext(ChatMentionsContext);
  if (context === undefined) {
    throw new Error('useMentions must be used within a MentionsProvider');
  }
  return context;
}
